import { Injectable } from '@angular/core';
import { BehavioralFactorRanges } from '../core-data-model/BehavioralFactorRanges.model';
import { BulkUpdateType } from '../core-data-model/BulkUpdateApi.connectinterface';
import { MinMax } from '../core-data-model/Common.model';
import { FilterInfo, FilterType } from '../core-data-model/FilterInfo.model';
import { Job, JobUpdate } from '../core-data-model/Job.model';
import { SearchResult } from '../core-data-model/SearchResult.model';
import { SearchState } from '../core-data-model/SearchState.model';
import { BenchmarkOccupationApi } from '../core-services/BenchmarkOccupationApi/BenchmarkOccupationApi.connect';
import { BulkUpdateApi } from '../core-services/BulkUpdateApi.connect';
import { BulkUpdateTransformers } from '../core-services/BulkUpdateApi.datatransform';
import { SearchApi } from '../core-services/SearchApi.connect';
import { StoreDataService } from '../core-services/Store.dataservice';

// Service for creating, updating, managing jobs on the server
@Injectable({
    providedIn: 'root'
})
export class JobService {
    constructor(
        private bulkUpdateApi: BulkUpdateApi,
        private bulkUpdateTransform: BulkUpdateTransformers,
        private benchmarkOccupationApi: BenchmarkOccupationApi,
        private searchApi: SearchApi,
        private store: StoreDataService
    ) {}

    async createJobAndUpdatePeopleAndSetTargetFromBenchmark(
        job: Job,
        people: SearchResult[]
    ): Promise<Job> {
        const behavioralRanges = this.getJobRangesFromTopBenchmark(job);
        if (behavioralRanges !== null) {
            await this.store.jobs.update(
                new JobUpdate({
                    originalObject: job,
                    behavioralRanges: behavioralRanges
                })
            );
        }
        await this.bulkUpdateApi.bulkUpdate(
            BulkUpdateType.UpdateJob,
            this.bulkUpdateTransform.transformBulkUpdateEntries(people),
            job.id
        );
        return job;
    }

    async getJobsPossibilities(filterText: string): Promise<Job[]> {
        if (filterText.trim() === '') {
            return [];
        }
        return this.searchApi.getPositions(filterText);
    }

    getJobRangesFromTopBenchmark(job: Job): BehavioralFactorRanges {
        if (!job.benchmarkProfiles.length) {
            return null;
        }
        const factorRanges = new BehavioralFactorRanges();
        const aRange = {
            min: job.benchmarkProfiles[0].behavioralInfo.aSigma - 0.5,
            max: job.benchmarkProfiles[0].behavioralInfo.aSigma + 0.5
        };
        const bRange = {
            min: job.benchmarkProfiles[0].behavioralInfo.bSigma - 0.5,
            max: job.benchmarkProfiles[0].behavioralInfo.bSigma + 0.5
        };
        const cRange = {
            min: job.benchmarkProfiles[0].behavioralInfo.cSigma - 0.5,
            max: job.benchmarkProfiles[0].behavioralInfo.cSigma + 0.5
        };
        const dRange = {
            min: job.benchmarkProfiles[0].behavioralInfo.dSigma - 0.5,
            max: job.benchmarkProfiles[0].behavioralInfo.dSigma + 0.5
        };
        factorRanges.A = new MinMax(aRange);
        factorRanges.B = new MinMax(bRange);
        factorRanges.C = new MinMax(cRange);
        factorRanges.D = new MinMax(dRange);
        return factorRanges;
    }

    private async isJobFolder(folderName: string): Promise<boolean> {
        // Check if a job family exists for the folder name
        const jobFamilies =
            await this.benchmarkOccupationApi.getPossibleOccupations(
                folderName
            );
        return jobFamilies.length > 0;
    }
    private async getPeopleWithoutJobsInFolder(
        currentSearchState: SearchState
    ): Promise<SearchResult[]> {
        // Add filter to current search for people in the folder w/out jobs
        const peopleWithoutJobs = await this.searchApi.searchPeopleAndJobs(
            currentSearchState.addFilter(
                new FilterInfo({
                    type: FilterType.PeopleWithoutJobs
                })
            )
        );
        return peopleWithoutJobs;
    }
    async getPeopleWithoutJobsInJobFolder(
        folderName: string,
        currentSearchState: SearchState
    ): Promise<SearchResult[]> {
        const isJobFolder = await this.isJobFolder(folderName);
        if (!isJobFolder) {
            return [];
        }
        return this.getPeopleWithoutJobsInFolder(currentSearchState);
    }
}
